// src/components/Chat/ChatInput.tsx
import React from "react";
import { GoImage } from "react-icons/go";
import { CgAttachment } from "react-icons/cg";
import { MdOutlineCancel } from "react-icons/md";
import { VscSend } from "react-icons/vsc";
import { CiStop1 } from "react-icons/ci";

interface ChatInputProps {
  selectedImage: string | null;
  file: File | null;
  inputMessage: string;
  setInputMessage: (message: string) => void;
  loading: boolean;
  fetchData: () => void;
  handleImageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: () => void;
  handleStopGeneration: () => void;
  selectedModel: string;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  getFileLogo: (fileType: any) => JSX.Element;
  width?: number;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

const ChatInput: React.FC<ChatInputProps> = ({
  selectedImage,
  file,
  inputMessage,
  setInputMessage,
  loading,
  fetchData,
  handleImageChange,
  handleRemoveImage,
  handleStopGeneration,
  selectedModel,
  handleFileChange,
  getFileLogo,
  width,
  inputRef,
}) => {
  return (
    <div className="flex flex-col items-center justify-center">
      <div
        className={`fixed bottom-2 max-md:px-4 flex flex-col`}
        style={{
          minWidth: "300px",
          maxWidth: "650px",
          width: width ? `${width}px` : "650px",
          transition: "width 0.1s ease-in-out",
        }}
      >
        {/* Images Start */}
        {selectedImage && (
          <div className="flex bg-white rounded-md p-4">
            <div className="relative">
              <img
                src={selectedImage}
                loading="lazy"
                alt={`Selected pic`}
                className="w-[60px] h-[60px] rounded-md object-cover"
              />
              <button
                className="absolute top-1 right-1 p-1 bg-red-600 hover:bg-red-700 duration-300 text-white rounded-full"
                onClick={handleRemoveImage}
              >
                <MdOutlineCancel size={10} />
              </button>
            </div>
          </div>
        )}
        {file && (
          <div className="flex flex-row gap-2 items-center rounded-2xl border-[#EDEAF8] p-2 bg-white border-t border-r border-l">
            <p className="pl-4">{getFileLogo(file.type)}</p>
            <p>{file.name}</p>
          </div>
        )}
        {/* Images End */}
        <div className="flex flex-row gap-2">
          <div className="w-full flex flex-row items-center bg-white rounded-2xl border border-gray-300">
            {/* Upload Image Start */}
            <div>
              <label htmlFor="imageInput" className="cursor-pointer">
                <input
                  type="file"
                  id="imageInput"
                  accept=".png, .jpg, .jpeg"
                  className="hidden"
                  multiple={false}
                  onChange={handleImageChange}
                />
                <div className="pl-4 rounded-md cursor-pointer">
                  <GoImage size={20} />
                </div>
              </label>
            </div>
            {selectedModel === "CodeLlama" && (
              <div>
                <label htmlFor="exelInput" className="cursor-pointer">
                  <input
                    type="file"
                    id="exelInput"
                    accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                    className="hidden"
                    multiple={false}
                    onChange={handleFileChange}
                  />
                  <div className="pl-4 rounded-md cursor-pointer">
                    <CgAttachment size={20} />
                  </div>
                </label>
              </div>
            )}
            {/* Upload Image End */}
            <textarea
              autoFocus
              ref={inputRef}
              value={inputMessage}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !loading && !e.shiftKey) {
                  fetchData();
                  e.preventDefault();
                }
              }}
              onChange={(e) => setInputMessage(e.target.value)}
              className={`w-full mx-auto pr-16 bg-white outline-none p-4 resize-none rounded-2xl ${
                inputMessage.length <= 134
                  ? "h-[55px] custom-scrollbar"
                  : "h-[140px] custom-scrollbar-chat"
              }`}
              placeholder="Send a message"
              maxLength={1000}
            />
            {loading ? (
              <div className="flex justify-end py-3">
                <div
                  onClick={handleStopGeneration}
                  className="bg-[#EDEAF8] border-[#EDEAF8] p-1 rounded-md hover:bg-[#b5b1c4] duration-300 cursor-pointer mr-3"
                >
                  <CiStop1 size={20} />
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fetchData()}
                disabled={loading || inputMessage.length === 0}
                className={`mr-3 ${
                  inputMessage.length !== 0
                    ? "bg-[#135991] text-white p-1 rounded-md"
                    : "p-1"
                } ${loading ? "bg-[#163752] rounded-md" : ""}`}
              >
                <VscSend size={20} />
              </button>
            )}
          </div>
        </div>
        <div>
          <span
            className={`flex justify-end ${
              inputMessage.length === 1000 ? "text-[red]" : "text-gray-500"
            }`}
          >
            {inputMessage.length}/1000
          </span>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
